import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { useDriverProfileAutofill } from './useDriverProfileAutofill';
import { checkDriverTable } from '../utils/checkDriverTable';
import { 
  DriverProfileData, 
  PersonalInfo, 
  VehicleInfo, 
  DriverInfo,
  ProfileSearchResult 
} from '../components/driver-profile/types';

export function useDriverProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [vehicleType, setVehicleType] = useState<'auto' | 'motorcycle'>('auto');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingProfiles, setExistingProfiles] = useState<DriverProfileData[]>([]);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [showVehicleSelection, setShowVehicleSelection] = useState(true);
  const [searchRaceNumber, setSearchRaceNumber] = useState('');
  const [searchDriverName, setSearchDriverName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<ProfileSearchResult[]>([]);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isOwnerSameAsDriver, setIsOwnerSameAsDriver] = useState(false);
  const [raceNumberError, setRaceNumberError] = useState<string | null>(null);
  
  const { 
    loading: autofillLoading, 
    error: autofillError, 
    populateDriverProfile,
    fetchDriverFullProfileByName,
    checkRaceNumberExists
  } = useDriverProfileAutofill();

  // Initialize form data
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    fullName: '',
    address: '',
    idNumber: '',
    dateOfBirth: '',
    nationality: '',
    country: '',
    mobilePhone: '',
    email: '',
    whatsapp: '',
    representativeOfOwner: ''
  });

  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo>({
    vehicleNumber: '',
    raceNumber: '',
    vehicleBrandModel: '', // Combined field for brand and model
    color: '',
    engineBrand: '',
    year: '',
    numberOfCylinders: ''
  });

  const [driverInfo, setDriverInfo] = useState<DriverInfo>({
    driverName: '',
    licenseNumber: '',
    expirationDate: '',
    teamName: '',
    clubName: '',
    nickname: '',
    address: '',
    dateOfBirth: '',
    nationality: '',
    country: '',
    mobilePhone: '',
    email: user?.email || '',
    idNumber: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadExistingProfiles();
    
    // Check driver table
    checkDriverTable().then(result => {
      console.log("Driver table check complete:", result);
    });
  }, [user, navigate]);

  // Effect to update personal info when driver info changes and isOwnerSameAsDriver is true
  useEffect(() => {
    if (isOwnerSameAsDriver) {
      setPersonalInfo({
        ...personalInfo,
        fullName: driverInfo.driverName,
        address: driverInfo.address,
        idNumber: driverInfo.idNumber,
        dateOfBirth: driverInfo.dateOfBirth,
        nationality: driverInfo.nationality,
        country: driverInfo.country,
        mobilePhone: driverInfo.mobilePhone,
        email: driverInfo.email
      });
    }
  }, [isOwnerSameAsDriver, driverInfo]);

  // Handle real-time search as user types
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Don't search if input is too short
    if (searchDriverName.length < 3) {
      setSearchResults([]);
      return;
    }

    // Set a new timeout to debounce the search
    const timeout = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Perform the search
        const result = await fetchDriverFullProfileByName(searchDriverName);
        if (result) {
          setSearchResults([result]);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching for driver:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    setSearchTimeout(timeout);

    // Cleanup function
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchDriverName]);

  // Validate race number when it changes
  useEffect(() => {
    const validateRaceNumber = async () => {
      if (vehicleInfo.raceNumber && vehicleInfo.raceNumber.trim() !== '') {
        const { exists, driverName } = await checkRaceNumberExists(vehicleInfo.raceNumber);
        
        if (exists) {
          // If the race number exists but doesn't match the current driver name
          if (driverName && driverInfo.driverName && 
              driverName.toLowerCase() !== driverInfo.driverName.toLowerCase()) {
            setRaceNumberError(`Race number "${vehicleInfo.raceNumber}" is already used by ${driverName}`);
          } else {
            setRaceNumberError(null);
          }
        } else {
          setRaceNumberError(null);
        }
      } else {
        setRaceNumberError(null);
      }
    };
    
    validateRaceNumber();
  }, [vehicleInfo.raceNumber, driverInfo.driverName]);

  const loadExistingProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('driver_profiles')
        .select('*')
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error loading profiles:', error);
        return;
      }

      if (data && data.length > 0) {
        setExistingProfiles(data.map(profile => ({
          id: profile.id,
          vehicleType: profile.vehicle_type,
          personalInfo: profile.personal_info,
          vehicleInfo: profile.vehicle_info,
          driverInfo: profile.driver_info,
          isComplete: profile.is_complete
        })));
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  const loadProfileData = (profile: DriverProfileData) => {
    setVehicleType(profile.vehicleType);
    setPersonalInfo(profile.personalInfo);
    setVehicleInfo(profile.vehicleInfo);
    setDriverInfo(profile.driverInfo);
    setEditingProfileId(profile.id || null);
    
    // Start with driver info step
    setCurrentStep(4);
    setShowVehicleSelection(false);
  };

  const startNewRegistration = (type: 'auto' | 'motorcycle') => {
    setVehicleType(type);
    setCurrentStep(4); // Start with driver information
    setEditingProfileId(null);
    setShowVehicleSelection(false);
    
    // Reset form data
    setPersonalInfo({
      fullName: '',
      address: '',
      idNumber: '',
      dateOfBirth: '',
      nationality: '',
      country: '',
      mobilePhone: '',
      email: '',
      whatsapp: '',
      representativeOfOwner: ''
    });
    setVehicleInfo({
      vehicleNumber: '',
      raceNumber: '',
      vehicleBrandModel: '', // Reset combined field
      color: '',
      engineBrand: '',
      year: '',
      numberOfCylinders: ''
    });
    setDriverInfo({
      driverName: '',
      licenseNumber: '',
      expirationDate: '',
      teamName: '',
      clubName: '',
      nickname: '',
      address: '',
      dateOfBirth: '',
      nationality: '',
      country: '',
      mobilePhone: '',
      email: user?.email || '',
      idNumber: ''
    });
    setIsOwnerSameAsDriver(false);
    setRaceNumberError(null);
  };

  const checkRaceNumberUniqueness = async (raceNumber: string, excludeProfileId?: string) => {
    if (!raceNumber.trim()) return true; // Empty race number is allowed during intermediate saves
    
    try {
      let query = supabase
        .from('driver_profiles')
        .select('id, vehicle_info')
        .neq('user_id', user?.id); // Exclude current user's profiles
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error checking race number:', error);
        return false;
      }
      
      // Check if any other driver has this race number
      const hasConflict = data?.some(profile => 
        profile.vehicle_info?.raceNumber === raceNumber &&
        profile.id !== excludeProfileId
      );
      
      return !hasConflict;
    } catch (error) {
      console.error('Error checking race number uniqueness:', error);
      return false;
    }
  };

  const saveProgress = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      const profileData = {
        user_id: user.id,
        vehicle_type: vehicleType,
        personal_info: personalInfo,
        vehicle_info: vehicleInfo,
        driver_info: driverInfo,
        is_complete: currentStep === 2,
        updated_at: new Date().toISOString()
      };

      if (editingProfileId) {
        // Update existing profile
        const { error } = await supabase
          .from('driver_profiles')
          .update(profileData)
          .eq('id', editingProfileId);

        if (error) throw error;
      } else {
        // Use UPSERT to handle existing profiles
        console.log('Attempting to save profile with race number:', vehicleInfo?.raceNumber);
        const { error } = await supabase
          .from('driver_profiles')
          .upsert([profileData], {
            onConflict: 'user_id,vehicle_type',
            ignoreDuplicates: false
          });

        if (error) {
          console.error('Profile save error details:', error);
          if (error.message.includes('idx_driver_profiles_race_number_unique')) {
            toast.error(`Race number "${vehicleInfo?.raceNumber}" is already taken by another driver. Please choose a different number.`);
            return;
          }
          throw error;
        }
      }
      
      toast.success('Progress saved successfully');
      loadExistingProfiles(); // Refresh the profiles list
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!await validateCurrentStep()) return;
    
    // If we're on the driver info step (4), go to vehicle info (3)
    if (currentStep === 4) {
      setCurrentStep(3);
    }
    // If we're on the vehicle info step (3), go to personal info (2)
    else if (currentStep === 3) {
      setCurrentStep(2);
    }
    // If we're on the personal info step (2), we're done
    else if (currentStep === 2) {
      await handleSubmit();
    }
    
    saveProgress();
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setCurrentStep(4);
    } else if (currentStep === 4) {
      setShowVehicleSelection(true);
      setCurrentStep(1);
    }
  };

  const validateCurrentStep = async () => {
    switch (currentStep) {
      case 1:
        return vehicleType !== '';
      case 2: // Vehicle Owner Info
        console.log("Validating vehicle owner info:", personalInfo);
        if (!personalInfo.fullName) {
          toast.error("Please enter the owner's full name");
          return false;
        }
        if (!personalInfo.address) {
          toast.error("Please enter the owner's address");
          return false;
        }
        if (!personalInfo.idNumber) {
          toast.error("Please enter the owner's ID number");
          return false;
        }
        if (!personalInfo.dateOfBirth) {
          toast.error("Please enter the owner's date of birth");
          return false;
        }
        if (!personalInfo.mobilePhone) {
          toast.error("Please enter the owner's mobile phone number");
          return false;
        }
        return true;
      case 3: // Vehicle Info
        console.log("Validating vehicle info:", vehicleInfo);
        if (!vehicleInfo.vehicleNumber) {
          toast.error("Please enter the vehicle number");
          return false;
        }
        if (!vehicleInfo.raceNumber) {
          toast.error("Please enter the race number");
          return false;
        }
        if (raceNumberError) {
          toast.error(raceNumberError);
          return false;
        }
        if (!vehicleInfo.vehicleBrandModel) {
          toast.error("Please enter the vehicle brand and model");
          return false;
        }
        if (!vehicleInfo.year) {
          toast.error("Please enter the vehicle year");
          return false;
        }
        
        // Check race number uniqueness
        const isRaceNumberUnique = await checkRaceNumberUniqueness(vehicleInfo.raceNumber, editingProfileId);
        if (!isRaceNumberUnique) {
          toast.error(`Race number "${vehicleInfo.raceNumber}" is already taken by another driver`);
          return false;
        }
        
        return true;
      case 4: // Driver Info
        console.log("Validating driver info:", driverInfo);
        if (!driverInfo.driverName) {
          toast.error("Please enter the driver's name");
          return false;
        }
        if (!driverInfo.address) {
          toast.error("Please enter the driver's address");
          return false;
        }
        if (!driverInfo.dateOfBirth) {
          toast.error("Please enter the driver's date of birth");
          return false;
        }
        if (!driverInfo.mobilePhone) {
          toast.error("Please enter the driver's mobile phone number");
          return false;
        }
        if (!driverInfo.idNumber) {
          toast.error("Please enter the driver's ID number");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!await validateCurrentStep()) return;

    setLoading(true);
    try {
      await saveProgress();
      toast.success('Driver profile completed successfully!');
      setShowVehicleSelection(true);
      setCurrentStep(1);
      loadExistingProfiles();
    } catch (error) {
      console.error('Error completing profile:', error);
      toast.error('Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRaceNumber = async () => {
    if (!searchRaceNumber.trim()) {
      toast.error('Please enter a race number to search');
      return;
    }

    setIsSearching(true);
    try {
      const success = await populateDriverProfile(
        searchRaceNumber,
        vehicleType,
        setPersonalInfo,
        setVehicleInfo,
        setDriverInfo,
        true // isRaceNumber = true
      );

      if (success) {
        toast.success('Driver information found and populated');
      } else if (autofillError) {
        toast.error(autofillError);
      } else {
        toast.error('No driver found with this race number');
      }
    } catch (error) {
      console.error('Error searching race number:', error);
      toast.error('Failed to search race number');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchDriverName = async () => {
    if (!searchDriverName.trim()) {
      toast.error('Please enter a driver name to search');
      return;
    }

    setIsSearching(true);
    try {
      const success = await populateDriverProfile(
        searchDriverName,
        vehicleType,
        setPersonalInfo,
        setVehicleInfo,
        setDriverInfo,
        false // isRaceNumber = false
      );

      if (success) {
        toast.success('Driver information found and populated');
        setSearchResults([]);
      } else if (autofillError) {
        toast.error(autofillError);
      } else {
        toast.error('No driver found with this name');
      }
    } catch (error) {
      console.error('Error searching driver name:', error);
      toast.error('Failed to search driver name');
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchResult = async (profile: any) => {
    setIsSearching(true);
    try {
      // Populate driver info
      setDriverInfo(prevInfo => ({
        ...prevInfo,
        driverName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
        nickname: profile?.nickname || prevInfo.nickname,
        teamName: profile?.team_name || prevInfo.teamName,
        clubName: profile?.club_name || prevInfo.clubName,
        address: profile?.address || prevInfo.address,
        dateOfBirth: profile?.date_of_birth || prevInfo.dateOfBirth,
        nationality: profile?.nationality || prevInfo.nationality,
        country: profile?.country || prevInfo.country,
        mobilePhone: profile?.phone ? String(profile.phone).replace('.0', '') : prevInfo.mobilePhone,
        idNumber: profile?.id_number || prevInfo.idNumber
      }));

      // Populate vehicle info with combined field
      setVehicleInfo(prevInfo => ({
        ...prevInfo,
        raceNumber: profile?.race_number || prevInfo.raceNumber,
        vehicleBrandModel: profile?.vehicle_model || 
                          `${profile?.car_make || ''} ${profile?.car_model || ''}`.trim(),
        year: profile?.year_of_model?.toString() || prevInfo.year
      }));

      // Populate personal info (vehicle owner info)
      setPersonalInfo(prevInfo => ({
        ...prevInfo,
        representativeOfOwner: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
      }));

      toast.success('Driver information populated successfully');
      setSearchResults([]);
      setSearchDriverName('');
    } catch (error) {
      console.error('Error populating driver profile:', error);
      toast.error('Failed to populate driver profile');
    } finally {
      setIsSearching(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Vehicle Registrations';
      case 2: return 'Vehicle Owner Information';
      case 3: return 'Vehicle Information';
      case 4: return 'Driver Information';
      default: return '';
    }
  };

  return {
    // State
    currentStep,
    vehicleType,
    loading,
    saving,
    existingProfiles,
    editingProfileId,
    showVehicleSelection,
    searchRaceNumber,
    searchDriverName,
    isSearching,
    searchResults,
    isOwnerSameAsDriver,
    raceNumberError,
    personalInfo,
    vehicleInfo,
    driverInfo,
    autofillLoading,
    autofillError,
    
    // Setters
    setCurrentStep,
    setVehicleType,
    setSearchRaceNumber,
    setSearchDriverName,
    setIsOwnerSameAsDriver,
    setPersonalInfo,
    setVehicleInfo,
    setDriverInfo,
    
    // Actions
    loadExistingProfiles,
    loadProfileData,
    startNewRegistration,
    saveProgress,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleSearchRaceNumber,
    handleSearchDriverName,
    selectSearchResult,
    getStepTitle
  };
} 